import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { X, Plus } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import RichTextEditor from "@/components/ui/RichTextEditor";
import type { EditingProduct, SizeGuide, ImageWithPreview, ProductFormData } from "@/types/product";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface AdminProductFormProps {
  productToEdit?: EditingProduct | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const AdminProductForm = ({ productToEdit, onSuccess, onCancel }: AdminProductFormProps) => {
  const [images, setImages] = useState<ImageWithPreview[]>(
    productToEdit?.images.map((url, index) => ({
      url,
      isMain: index === 0,
    })) || []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [sizesInput, setSizesInput] = useState(productToEdit?.variants
    ? Array.from(new Set(productToEdit.variants.map(v => v.size).filter(Boolean))).join(", ")
    : "");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  console.log("AdminProductForm: Initializing with product:", productToEdit);

  const { data: categoriesData, isLoading: isCategoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      console.log("AdminProductForm: Fetching categories");
      const { data, error } = await supabase
        .from('categories')
        .select('name')
        .order('name');

      if (error) {
        console.error("AdminProductForm: Error fetching categories:", error);
        throw error;
      }

      const categoryNames = data.map(c => c.name);
      console.log("AdminProductForm: Loaded categories:", categoryNames);
      setCategories(categoryNames);
      return categoryNames;
    }
  });

  const addNewCategory = async () => {
    const categoryName = newCategory.trim();
    if (!categoryName) return;

    try {
      console.log("AdminProductForm: Adding new category:", categoryName);
      const { error } = await supabase
        .from('categories')
        .insert({ name: categoryName });

      if (error) {
        console.error("AdminProductForm: Error adding category:", error);
        throw error;
      }

      setCategories(prev => [...prev, categoryName]);
      setNewCategory("");
      
      toast({
        title: "Catégorie ajoutée",
        description: "La nouvelle catégorie a été ajoutée avec succès"
      });
    } catch (error) {
      console.error("AdminProductForm: Error in addNewCategory:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter la catégorie",
        variant: "destructive"
      });
    }
  };

  const initialDimensions = productToEdit?.size_guide
    ? Object.values(productToEdit.size_guide)[0]
      ? Object.keys(Object.values(productToEdit.size_guide)[0]).map(name => ({
          id: crypto.randomUUID(),
          name
        }))
      : [
          { id: crypto.randomUUID(), name: 'Largeur' },
          { id: crypto.randomUUID(), name: 'Longueur' }
        ]
    : [
        { id: crypto.randomUUID(), name: 'Largeur' },
        { id: crypto.randomUUID(), name: 'Longueur' }
      ];

  const form = useForm<ProductFormData>({
    defaultValues: {
      name: productToEdit?.name || "",
      description: productToEdit?.description || "",
      price: productToEdit?.price || 0,
      categories: productToEdit?.categories || [],
      colors: productToEdit?.colors || [],
      sizes: productToEdit?.variants
        ? Array.from(new Set(productToEdit.variants.map(v => v.size).filter(Boolean)))
        : [],
      sizeGuide: productToEdit?.size_guide
        ? Object.entries(productToEdit.size_guide).map(([size, dimensions]) => ({
            size,
            dimensions: dimensions as { [key: string]: string }
          }))
        : [],
      hasSizes: productToEdit ? productToEdit.has_sizes : false,
      hasColors: productToEdit ? productToEdit.has_colors : false,
      hasSizeGuide: productToEdit ? productToEdit.has_size_guide : false,
      dimensions: initialDimensions,
    },
  });

  useEffect(() => {
    if (!form.watch("hasColors")) {
      console.log("Clearing colors as hasColors is false");
      form.setValue("colors", []);
    }
  }, [form.watch("hasColors")]);

  useEffect(() => {
    if (!form.watch("hasSizes")) {
      console.log("Clearing sizes as hasSizes is false");
      form.setValue("sizes", []);
      setSizesInput("");
    }
  }, [form.watch("hasSizes")]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newImages = Array.from(files).map(file => ({
        file,
        url: URL.createObjectURL(file),
        isMain: false,
      }));
      setImages(prev => [...prev, ...newImages]);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const setMainImage = (index: number) => {
    setImages(prev => prev.map((img, i) => ({
      ...img,
      isMain: i === index
    })));
  };

  const handleSizesInput = (value: string) => {
    setSizesInput(value);
    
    const sizesArray = Array.from(new Set(
      value.split(',')
        .map(size => size.trim())
        .filter(size => size !== '')
    ));
    form.setValue("sizes", sizesArray);
  };

  const removeSize = (index: number) => {
    const currentSizes = form.getValues("sizes");
    const sizeToRemove = currentSizes[index];
    
    form.setValue("sizes", currentSizes.filter((_, i) => i !== index));
    
    if (form.getValues("hasSizeGuide")) {
      const currentSizeGuide = form.getValues("sizeGuide");
      form.setValue("sizeGuide", currentSizeGuide.filter(entry => entry.size !== sizeToRemove));
    }
  };

  const addDimension = () => {
    const currentDimensions = form.getValues("dimensions");
    form.setValue("dimensions", [
      ...currentDimensions,
      { id: crypto.randomUUID(), name: "" }
    ]);
  };

  const updateDimensionName = (dimensionId: string, newName: string) => {
    const currentDimensions = form.getValues("dimensions");
    const updatedDimensions = currentDimensions.map(dim => 
      dim.id === dimensionId ? { ...dim, name: newName } : dim
    );
    form.setValue("dimensions", updatedDimensions);

    const currentSizeGuide = form.getValues("sizeGuide");
    const updatedSizeGuide = currentSizeGuide.map(entry => ({
      ...entry,
      dimensions: {
        ...entry.dimensions,
        [newName]: entry.dimensions[newName] || ""
      }
    }));
    form.setValue("sizeGuide", updatedSizeGuide);
  };

  const removeDimension = (dimensionId: string) => {
    const currentDimensions = form.getValues("dimensions");
    const dimensionToRemove = currentDimensions.find(d => d.id === dimensionId);
    
    if (!dimensionToRemove) return;
    
    if (['Largeur', 'Longueur'].includes(dimensionToRemove.name)) {
      toast({
        title: "Action non permise",
        description: "Les dimensions Largeur et Longueur sont obligatoires",
        variant: "destructive"
      });
      return;
    }
    
    form.setValue("dimensions", currentDimensions.filter(d => d.id !== dimensionId));
    
    const currentSizeGuide = form.getValues("sizeGuide");
    const updatedSizeGuide = currentSizeGuide.map(entry => {
      const { [dimensionToRemove.name]: removed, ...remainingDimensions } = entry.dimensions;
      return {
        ...entry,
        dimensions: remainingDimensions
      };
    });
    form.setValue("sizeGuide", updatedSizeGuide);
  };

  const addCustomSize = () => {
    const currentSizes = form.getValues("sizes");
    form.setValue("sizes", [...currentSizes, ""]);
  };

  const addSizeGuideEntry = () => {
    const currentSizeGuide = form.getValues("sizeGuide");
    const sizes = form.getValues("sizes");
    const dimensions = form.getValues("dimensions");

    const sizesWithoutGuide = sizes.filter(size => 
      !currentSizeGuide.some(entry => entry.size === size)
    );

    const newEntries = sizesWithoutGuide.map(size => ({
      size,
      dimensions: dimensions.reduce((acc, dimension) => ({
        ...acc,
        [dimension.name]: ""
      }), {} as { [key: string]: string })
    }));

    if (newEntries.length > 0) {
      form.setValue("sizeGuide", [...currentSizeGuide, ...newEntries]);
    }
  };

  const removeSizeGuideEntry = (index: number) => {
    const currentSizeGuide = form.getValues("sizeGuide");
    form.setValue("sizeGuide", currentSizeGuide.filter((_, i) => i !== index));
  };

  const addColor = () => {
    const colors = form.getValues("colors");
    form.setValue("colors", [...colors, { name: "", hex: "#000000" }]);
  };

  const removeColor = (index: number) => {
    const colors = form.getValues("colors");
    form.setValue("colors", colors.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: ProductFormData) => {
    try {
      setIsSubmitting(true);
      console.log("AdminProductForm: Starting submission with data:", data);

      if (!productToEdit && !images.length) {
        toast({
          title: "Erreur",
          description: "Veuillez ajouter au moins une image",
          variant: "destructive",
        });
        return;
      }

      try {
        const productData = {
          name: data.name,
          description: data.description,
          price: data.price,
          has_sizes: data.hasSizes,
          has_colors: data.hasColors,
          has_size_guide: data.hasSizeGuide,
          size_guide: data.hasSizeGuide && data.sizeGuide.length > 0
            ? data.sizeGuide.reduce((acc, entry) => {
                if (entry.size) {
                  acc[entry.size] = entry.dimensions;
                }
                return acc;
              }, {} as SizeGuide)
            : null,
          published: productToEdit ? productToEdit.published : false
        };

        console.log("AdminProductForm: Saving product data:", productData);

        let productId = productToEdit?.id;
        let error;

        if (productToEdit) {
          const { error: updateError } = await supabase
            .from('products')
            .update(productData)
            .eq('id', productToEdit.id);
          error = updateError;
        } else {
          const { data: newProduct, error: insertError } = await supabase
            .from('products')
            .insert(productData)
            .select('id')
            .single();

          if (insertError) {
            error = insertError;
          } else {
            productId = newProduct.id;
          }
        }

        if (error) throw error;
        if (!productId) throw new Error("Product ID is missing");

        if (data.categories.length > 0) {
          const { data: categoriesData, error: fetchCategoriesError } = await supabase
            .from('categories')
            .select('id, name')
            .in('name', data.categories);

          if (fetchCategoriesError) throw fetchCategoriesError;

          const categoryLinks = categoriesData.map(category => ({
            product_id: productId,
            category_id: category.id
          }));

          if (productToEdit) {
            const { error: deleteCategoriesError } = await supabase
              .from('product_categories')
              .delete()
              .eq('product_id', productId);
            
            if (deleteCategoriesError) throw deleteCategoriesError;
          }

          if (categoryLinks.length > 0) {
            const { error: insertCategoriesError } = await supabase
              .from('product_categories')
              .insert(categoryLinks);

            if (insertCategoriesError) throw insertCategoriesError;
          }
        }

        if (productToEdit) {
          const { error: deleteVariantsError } = await supabase
            .from('product_variants')
            .delete()
            .eq('product_id', productId);
          
          if (deleteVariantsError) throw deleteVariantsError;
        }

        const variants = [];
        
        if (data.hasSizes && data.hasColors && data.sizes.length > 0 && data.colors.length > 0) {
          data.sizes.forEach((size, sizeIndex) => {
            data.colors.forEach((color, colorIndex) => {
              variants.push({
                product_id: productId,
                size: size.trim(),
                color_name: color.name.trim(),
                color_hex: color.hex,
                position: sizeIndex
              });
            });
          });
        } else if (data.hasSizes && data.sizes.length > 0) {
          data.sizes.forEach((size, index) => {
            variants.push({
              product_id: productId,
              size: size.trim(),
              color_name: null,
              color_hex: null,
              position: index
            });
          });
        } else if (data.hasColors && data.colors.length > 0) {
          data.colors.forEach((color, index) => {
            variants.push({
              product_id: productId,
              size: null,
              color_name: color.name.trim(),
              color_hex: color.hex,
              position: index
            });
          });
        }

        console.log("AdminProductForm: Creating variants with positions:", variants);

        if (variants.length > 0) {
          const { error: variantsError } = await supabase
            .from('product_variants')
            .insert(variants);

          if (variantsError) {
            console.error("Error creating variants:", variantsError);
            throw variantsError;
          }
        }

        const newImages = images.filter(img => img.file);
        if (newImages.length > 0 || productToEdit) {
          if (productToEdit) {
            const { error: deleteImagesError } = await supabase
              .from('product_images')
              .delete()
              .eq('product_id', productId);
            
            if (deleteImagesError) throw deleteImagesError;
          }

          const uploadedImages = await Promise.all(
            images.map(async (image, index) => {
              let url = image.url;
              if (image.file) {
                const fileExt = image.file.name.split('.').pop();
                const fileName = `${crypto.randomUUID()}.${fileExt}`;

                const { error: uploadError } = await supabase.storage
                  .from('product-images')
                  .upload(fileName, image.file);

                if (uploadError) throw uploadError;

                const { data } = supabase.storage
                  .from('product-images')
                  .getPublicUrl(fileName);
                
                url = data.publicUrl;
              }

              return {
                product_id: productId,
                url,
                main: image.isMain
              };
            })
          );

          const { error: imagesError } = await supabase
            .from('product_images')
            .insert(uploadedImages);

          if (imagesError) throw imagesError;
        }

        queryClient.invalidateQueries({ queryKey: ['products'] });

        console.log("AdminProductForm: All data saved successfully");
        toast({
          title: "Succès",
          description: "Le produit a été enregistré avec succès",
        });
        onSuccess();
        
      } catch (error) {
        console.error("AdminProductForm: Error in form submission:", error);
        throw error;
      }

    } catch (error) {
      console.error("AdminProductForm: Error in form submission:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la sauvegarde du produit",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">
          {productToEdit ? "Modifier le produit" : "Nouveau produit"}
        </h2>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom du produit</FormLabel>
                  <FormControl>
                    <Input placeholder="Nom du produit" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <RichTextEditor value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prix</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder="Prix"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium mb-2">Catégories</h3>
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder="Nouvelle catégorie..."
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addNewCategory();
                      }
                    }}
                  />
                  <Button type="button" onClick={addNewCategory}>
                    Ajouter
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {isCategoriesLoading ? (
                    <div>Chargement des catégories...</div>
                  ) : (
                    categories.map((category) => (
                      <label
                        key={category}
                        className="flex items-center space-x-2 border rounded-lg p-2 cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={form.watch("categories").includes(category)}
                          onChange={(e) => {
                            const current = form.watch("categories");
                            if (e.target.checked) {
                              form.setValue("categories", [...current, category]);
                            } else {
                              form.setValue("categories", current.filter(c => c !== category));
                            }
                          }}
                          className="h-4 w-4"
                        />
                        <span>{category}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="hasSizes"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (!e.target.checked) {
                          form.setValue("sizes", []);
                        }
                      }}
                      className="h-4 w-4"
                    />
                  </FormControl>
                  <FormLabel className="m-0">Ce produit a des tailles</FormLabel>
                </FormItem>
              )}
            />

            {form.watch("hasSizes") && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Tailles disponibles</h3>
                </div>
                <div className="space-y-4">
                  <Input
                    placeholder="Entrez les tailles séparées par des virgules (ex: XS, S, M, L, XL)"
                    onChange={(e) => handleSizesInput(e.target.value)}
                    value={sizesInput}
                  />
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {form.watch("sizes").map((size, index) => (
                      <div key={index} className="flex gap-2 items-center bg-gray-50 p-2 rounded">
                        <span className="flex-1">{size}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSize(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="hasColors"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (!e.target.checked) {
                          form.setValue("colors", []);
                        }
                      }}
                      className="h-4 w-4"
                    />
                  </FormControl>
                  <FormLabel className="m-0">Ce produit a des couleurs</FormLabel>
                </FormItem>
              )}
            />

            {form.watch("hasColors") && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Couleurs disponibles</h3>
                  <Button type="button" onClick={addColor} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une couleur
                  </Button>
                </div>
                <div className="space-y-4">
                  {form.watch("colors").map((color, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <Input
                        placeholder="Nom de la couleur"
                        value={color.name}
                        onChange={(e) => {
                          const colors = [...form.getValues("colors")];
                          colors[index].name = e.target.value;
                          form.setValue("colors", colors);
                        }}
                      />
                      <Input
                        type="color"
                        value={color.hex}
                        onChange={(e) => {
                          const colors = [...form.getValues("colors")];
                          colors[index].hex = e.target.value;
                          form.setValue("colors", colors);
                        }}
                        className="w-20 p-1 h-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeColor(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {form.watch("hasSizeGuide") && form.watch("hasSizes") && (
              <div className="space-y-4 border-t pt-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Guide des tailles</h3>
                  <div className="flex gap-2">
                    <Button type="button" onClick={addDimension} variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter une dimension
                    </Button>
                  </div>
                </div>

                <div className="space-y-4 mb-4">
                  <h4 className="text-sm font-medium">Dimensions disponibles</h4>
                  <div className="grid gap-4">
                    {form.watch("dimensions").map((dimension) => (
                      <div key={dimension.id} className="flex items-center gap-2">
                        <Input
                          value={dimension.name}
                          onChange={(e) => updateDimensionName(dimension.id, e.target.value)}
                          placeholder="Nom de la dimension (ex: Tour de poitrine)"
                        />
                        {!['Largeur', 'Longueur'].includes(dimension.name) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeDimension(dimension.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  {form.watch("sizes").map((size, sizeIndex) => {
                    const sizeGuideEntry = form.watch("sizeGuide")?.find(entry => entry.size === size);
                    return (
                      <div key={size} className="grid gap-4">
                        <FormField
                          control={form.control}
                          name={`sizeGuide.${sizeIndex}.size`}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormLabel>Taille {size}</FormLabel>
                              <FormControl>
                                <Input value={size} readOnly className="bg-gray-50" />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {form.watch("dimensions").map((dimension) => (
                            <FormField
                              key={dimension.id}
                              control={form.control}
                              name={`sizeGuide.${sizeIndex}.dimensions.${dimension.name}`}
                              render={({ field }) => (
                                <FormItem className="flex-1">
                                  <FormLabel>{dimension.name}</FormLabel>
                                  <FormControl>
                                    <Input
                                      type="text"
                                      placeholder="Valeur en cm"
                                      value={sizeGuideEntry?.dimensions[dimension.name] || ''}
                                      onChange={(e) => {
                                        const currentSizeGuide = [...(form.getValues("sizeGuide") || [])];
                                        const entryIndex = currentSizeGuide.findIndex(e => e.size === size);
                                        
                                        if (entryIndex === -1) {
                                          currentSizeGuide.push({
                                            size,
                                            dimensions: {
                                              [dimension.name]: e.target.value
                                            }
                                          });
                                        } else {
                                          currentSizeGuide[entryIndex].dimensions = {
                                            ...currentSizeGuide[entryIndex].dimensions,
                                            [dimension.name]: e.target.value
                                          };
                                        }
                                        
                                        form.setValue("sizeGuide", currentSizeGuide);
                                      }}
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Images du produit
            </label>
            <div className="flex flex-wrap gap-4">
              {images.map((image, index) => (
                <div key={index} className="relative">
                  <img
                    src={image.url}
                    alt={`Preview ${index + 1}`}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                  <div className="absolute -top-2 -right-2 flex gap-1">
                    <Button
                      type="button"
                      variant={image.isMain ? "default" : "outline"}
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setMainImage(index)}
                    >
                      ★
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => removeImage(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <label className="w-24 h-24 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <Plus className="h-6 w-6 text-gray-400" />
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-8">
            <Button type="button" variant="outline" onClick={onCancel}>
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting 
                ? (productToEdit ? "Mise à jour en cours..." : "Création en cours...") 
                : (productToEdit ? "Mettre à jour" : "Créer le produit")
              }
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AdminProductForm;

